/**
 * RootErrorBoundary — error boundary global, capte les crashes inattendus.
 *
 * @remarks
 * Différent du `PanelErrorBoundary` (qui isole un panel) : celui-ci attrape
 * les erreurs qui ont remonté tout l'arbre. C'est le dernier filet — il
 * affiche une page d'erreur statique avec un bouton "recharger".
 *
 * À placer **au-dessus** des providers TanStack/Theme/Toast pour que même
 * un crash dans ces providers soit récupérable.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Heading, Button } from '@/shared/ui';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class RootErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // En prod : envoyer à un service de logging (Sentry, etc.)
    console.error('[RootErrorBoundary] Crash app racine:', error, info.componentStack);
  }

  override render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="bg-bg text-fg flex min-h-screen flex-col items-center justify-center gap-4 p-8">
        <Heading level="page" className="text-2xl">
          Une erreur est survenue
        </Heading>
        <p className="text-fg-muted max-w-md text-center">
          L'application a rencontré un problème inattendu. Tu peux essayer de recharger la page.
        </p>
        {this.state.error && (
          <pre className="bg-bg-muted text-fg-muted max-w-2xl overflow-auto rounded-sm p-4 text-xs">
            {this.state.error.message}
          </pre>
        )}
        <Button
          variant="primary"
          size="md"
          onClick={() => {
            window.location.reload();
          }}
        >
          Recharger
        </Button>
      </div>
    );
  }
}
